<template>
  <div class="keno-game">
    <!-- Game Topbar -->
    <div class="game-topbar">
      <button class="btn-back" @click="router.push({ name: 'player.lobby' })">{{ $t('games.back_to_casino') }}</button>
      <span class="game-title-bar">
        <AppIcon name="game-keno" :size="18" /> {{ $t('games.keno.title') }}
        <span class="game-tag">{{ $t('games.keno.tag') }}</span>
      </span>
      <div class="game-topbar-actions">
        <button
          class="icon-btn"
          :class="{ 'icon-btn-active': soundEnabled }"
          :title="soundEnabled ? $t('games.sound_on') : $t('games.sound_off')"
          @click="soundEnabled = !soundEnabled"
        >
          <AppIcon :name="soundEnabled ? 'sound-on' : 'sound-off'" :size="16" />
        </button>
        <button
          class="icon-btn"
          :title="$t('games.provably_fair')"
          @click="router.push({ name: 'player.provably-fair' })"
        >
          <AppIcon name="provably-fair" :size="16" />
        </button>
      </div>
    </div>

    <!-- Game Body -->
    <div class="game-body">
      <!-- Canvas / Main Area -->
      <div class="game-canvas-area">
        <div class="canvas-bg-grid"></div>
        <div class="canvas-bg-glow"></div>

        <div class="keno-board">
          <!-- Selection counter + match count -->
          <div class="keno-stats-row">
            <span class="keno-pick-count">
              {{ $t('games.keno.selected_count', { count: selectedNumbers.size, max: maxPicks }) }}
            </span>
            <span v-if="lastResult" class="keno-match-count" :class="lastResult.isWin ? 'match-win' : 'match-loss'">
              {{ $t('games.keno.matches_count', { hits: lastResult.hits, picks: selectedNumbers.size || lastPickCount }) }}
              <span v-if="lastResult.isWin" class="match-payout"> · +${{ lastResult.payout.toFixed(2) }}</span>
            </span>
          </div>

          <!-- Keno Grid -->
          <div class="keno-grid" :class="gridClass">
            <button
              v-for="n in gridSize"
              :key="n"
              class="keno-ball"
              :class="ballClass(n)"
              :disabled="isDrawing && !drawnSet.has(n)"
              @click="toggleSelection(n)"
            >
              {{ n }}
            </button>
          </div>

          <!-- Paytable overlay button -->
          <div class="keno-paytable-toggle">
            <button class="btn-paytable-toggle" @click="showPaytableOverlay = true">
              {{ $t('games.keno.paytable') }}
            </button>
          </div>

          <!-- History strip: last 20 draws -->
          <div v-if="history.length > 0" class="keno-history">
            <div class="history-label">{{ $t('games.keno.last_draws') }}</div>
            <div class="history-strip">
              <div
                v-for="(entry, idx) in history"
                :key="idx"
                class="history-pill"
                :class="entry.hits > 0 ? 'pill-win' : 'pill-loss'"
                :title="`${entry.hits}/${entry.picks} matches · ${entry.payout > 0 ? '+$' + entry.payout.toFixed(2) : 'Loss'}`"
              >
                {{ entry.hits }}/{{ entry.picks }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Sidebar -->
      <div class="game-sidebar">
        <!-- Tabs -->
        <div class="game-tabs">
          <button
            class="game-tab"
            :class="{ active: activeTab === 'bet' }"
            @click="activeTab = 'bet'"
          >
            {{ $t('games.bet_tab') }}
          </button>
          <button
            class="game-tab"
            :class="{ active: activeTab === 'paytable' }"
            @click="activeTab = 'paytable'"
          >
            {{ $t('games.keno.pays_label') }}
          </button>
          <button
            class="game-tab"
            :class="{ active: activeTab === 'rules' }"
            @click="activeTab = 'rules'"
          >
            {{ $t('games.rules_tab') }}
          </button>
        </div>

        <!-- Bet Tab -->
        <div v-show="activeTab === 'bet'" class="game-tab-content">
          <!-- Bet Amount -->
          <label class="ctrl-label">{{ $t('games.bet_amount') }}</label>
          <div class="bet-input-wrap">
            <span class="bet-currency">$</span>
            <input
              v-model.number="betAmount"
              type="number"
              class="bet-input"
              min="0.01"
              step="0.01"
              :disabled="isDrawing"
              @keydown.enter="placeBet"
            />
          </div>
          <div class="bet-shortcuts">
            <button class="bet-short" :disabled="isDrawing" @click="halveBet">½</button>
            <button class="bet-short" :disabled="isDrawing" @click="doubleBet">2×</button>
            <button class="bet-short" :disabled="isDrawing" @click="maxBet">Max</button>
          </div>

          <!-- Quick Pick + Clear -->
          <div class="keno-quick-row">
            <button class="btn-quick" :disabled="isDrawing || selectedNumbers.size >= maxPicks" @click="quickPick">
              {{ $t('games.keno.quick_pick') }}
            </button>
            <button class="btn-clear" :disabled="isDrawing || selectedNumbers.size === 0" @click="clearSelection">
              {{ $t('games.keno.clear') }}
            </button>
          </div>

          <!-- Speed Modes -->
          <label class="ctrl-label">{{ $t('games.keno.draw_speed') }}</label>
          <div class="speed-modes">
            <button
              class="speed-btn"
              :class="{ active: speedMode === 'normal' }"
              :disabled="isDrawing"
              @click="speedMode = 'normal'"
            >
              Normal
            </button>
            <button
              class="speed-btn"
              :class="{ active: speedMode === 'turbo' }"
              :disabled="isDrawing"
              @click="speedMode = 'turbo'"
            >
              Turbo
            </button>
            <button
              class="speed-btn"
              :class="{ active: speedMode === 'instant' }"
              :disabled="isDrawing"
              @click="speedMode = 'instant'"
            >
              Instant
            </button>
          </div>

          <!-- Stat Pills -->
          <div class="stat-pills">
            <div class="stat-pill">
              <div class="sp-label">Picks</div>
              <div class="sp-value">{{ selectedNumbers.size }}/{{ maxPicks }}</div>
            </div>
            <div class="stat-pill">
              <div class="sp-label">Jackpot</div>
              <div class="sp-value gold">{{ jackpotDisplay }}</div>
            </div>
          </div>

          <!-- Place Bet -->
          <button
            class="btn-action"
            :disabled="!canBet"
            @click="placeBet"
          >
            {{ isDrawing ? $t('common.processing') : $t('games.place_bet') }}
          </button>

          <!-- Balance -->
          <div class="balance-row">
            <span class="bal-label">{{ $t('games.balance') }}</span>
            <span class="bal-value green">{{ formattedBalance }}</span>
          </div>
        </div>

        <!-- Paytable Tab -->
        <div v-show="activeTab === 'paytable'" class="game-tab-content">
          <div class="sidebar-paytable">
            <div class="sp-header">
              <span class="sp-header-title">Paytable</span>
              <span class="sp-header-sub">{{ selectedNumbers.size > 0 ? `${selectedNumbers.size} picks selected` : 'Select picks to highlight' }}</span>
            </div>
            <div
              v-for="picks in 10"
              :key="picks"
              class="sp-picks-row"
              :class="{ 'sp-picks-active': selectedNumbers.size === picks }"
            >
              <div class="sp-picks-label">{{ picks }} pick{{ picks !== 1 ? 's' : '' }}</div>
              <div class="sp-hits-grid">
                <div
                  v-for="(mult, hitsIdx) in PAYTABLE[picks]"
                  :key="hitsIdx"
                  class="sp-cell"
                  :class="{
                    'sp-cell-win': mult > 0,
                    'sp-cell-zero': mult === 0,
                    'sp-cell-highlight': lastResult !== null && selectedNumbers.size === picks && lastResult.hits === hitsIdx,
                  }"
                  :title="`${hitsIdx} hit${hitsIdx !== 1 ? 's' : ''}: ${mult > 0 ? mult + '×' : '—'}`"
                >
                  <span class="sp-cell-hits">{{ hitsIdx }}h</span>
                  <span class="sp-cell-mult">{{ mult > 0 ? (mult >= 100 ? mult + '×' : mult + '×') : '—' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Rules Tab -->
        <div v-show="activeTab === 'rules'" class="game-tab-content">
          <div class="rules-section">
            <div class="rules-title"><span class="rules-title-icon"><AppIcon name="game-keno" :size="18" /></span> How to Play</div>
            <ul class="rules-list">
              <li class="rules-item">Select up to {{ maxPicks }} numbers from the grid</li>
              <li class="rules-item">Place your bet and the draw begins</li>
              <li class="rules-item">{{ drawCount }} numbers are randomly drawn</li>
              <li class="rules-item">Winnings depend on how many picks match the draw</li>
              <li class="rules-item">More matches = bigger payout</li>
            </ul>
            <div class="rules-note">
              Grid: {{ gridSize }} numbers · Draw: {{ drawCount }} numbers · Max picks: {{ maxPicks }}
            </div>
          </div>

          <!-- Paytable reference -->
          <div v-if="selectedNumbers.size > 0" class="paytable-ref">
            <div class="paytable-title">{{ $t('games.keno.paytable_for_picks', { picks: selectedNumbers.size }) }}</div>
            <div
              v-for="(mult, hits) in currentPaytableRow"
              :key="hits"
              class="paytable-row"
              :class="{ 'pt-highlighted': lastResult && lastResult.hits === Number(hits) }"
            >
              <span class="pt-hits">{{ hits }} hit{{ Number(hits) !== 1 ? 's' : '' }}</span>
              <span class="pt-mult" :class="mult > 0 ? 'pt-win' : 'pt-zero'">{{ mult > 0 ? `${mult}×` : '—' }}</span>
            </div>
          </div>

          <div class="fair-tag" @click="router.push({ name: 'player.provably-fair' })">
            <AppIcon name="provably-fair" :size="12" /> Provably Fair Game
          </div>
        </div>
      </div>
    </div>

    <!-- Paytable Overlay Modal -->
    <Transition name="modal-fade">
      <div v-if="showPaytableOverlay" class="paytable-overlay" @click.self="showPaytableOverlay = false">
        <div class="paytable-modal">
          <div class="paytable-modal-header">
            <span class="paytable-modal-title">{{ $t('games.keno.paytable_title') }}</span>
            <span class="paytable-modal-sub">{{ gridSize }} numbers · {{ drawCount }} drawn</span>
            <button class="paytable-close" @click="showPaytableOverlay = false">✕</button>
          </div>
          <div class="paytable-modal-body">
            <div class="paytable-legend">
              <span class="pl-current">■ Current selection</span>
              <span class="pl-result">■ Last result</span>
            </div>
            <!-- Full picks×hits matrix -->
            <div class="paytable-matrix">
              <div class="pm-row pm-header-row">
                <div class="pm-picks-col">Picks</div>
                <div v-for="h in 11" :key="h - 1" class="pm-cell pm-header-cell">
                  {{ h - 1 }}h
                </div>
              </div>
              <div
                v-for="p in 10"
                :key="p"
                class="pm-row"
                :class="{ 'pm-row-active': selectedNumbers.size === p }"
              >
                <div class="pm-picks-col" :class="{ 'pm-picks-active': selectedNumbers.size === p }">
                  {{ p }}
                </div>
                <div
                  v-for="hitsIdx in p + 1"
                  :key="hitsIdx - 1"
                  class="pm-cell"
                  :class="{
                    'pm-cell-win': (PAYTABLE[p]?.[hitsIdx - 1] ?? 0) > 0,
                    'pm-cell-zero': (PAYTABLE[p]?.[hitsIdx - 1] ?? 0) === 0,
                    'pm-cell-result': lastResult !== null && selectedNumbers.size === p && lastResult.hits === hitsIdx - 1,
                  }"
                >
                  {{ (PAYTABLE[p]?.[hitsIdx - 1] ?? 0) > 0 ? (PAYTABLE[p]?.[hitsIdx - 1] ?? 0) + '×' : '—' }}
                </div>
                <!-- Empty cells for hit counts that can't exist for this pick count -->
                <div v-for="emptyIdx in 10 - p" :key="`e-${emptyIdx}`" class="pm-cell pm-cell-na">·</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, reactive, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'
import AppIcon from '@/components/AppIcon.vue'
import { useWalletStore } from '@/stores/wallet'
import { useToastStore } from '@/stores/toast'

const { t } = useI18n()
const router = useRouter()
const walletStore = useWalletStore()
const toastStore = useToastStore()

// ── Game config (mirrors KenoGame.php defaults) ──────────────────────────────
const gridSize = ref<number>(80)
const drawCount = ref<number>(20)
const maxPicks = ref<number>(10)

// ── Speed mode ────────────────────────────────────────────────────────────────
type SpeedMode = 'normal' | 'turbo' | 'instant'
const speedMode = ref<SpeedMode>('normal')
const speedDelayMs = computed<number>(() => {
  if (speedMode.value === 'turbo') return 50
  if (speedMode.value === 'instant') return 0
  return 100
})

// ── Sound ─────────────────────────────────────────────────────────────────────
const soundEnabled = ref<boolean>(true)
let _audioCtx: AudioContext | null = null

function getAudioCtx(): AudioContext {
  if (!_audioCtx) _audioCtx = new AudioContext()
  return _audioCtx
}

function playBeep(type: 'draw' | 'hit'): void {
  if (!soundEnabled.value) return
  try {
    const ctx = getAudioCtx()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.frequency.value = type === 'hit' ? 900 : 440
    osc.type = 'sine'
    gain.gain.setValueAtTime(0.08, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12)
    osc.start(ctx.currentTime)
    osc.stop(ctx.currentTime + 0.12)
  } catch {
    // AudioContext may be blocked in some environments; fail silently
  }
}

// ── Paytable overlay ──────────────────────────────────────────────────────────
const showPaytableOverlay = ref<boolean>(false)

// Default paytable mirrors KenoGame::DEFAULT_PAYTABLE (picks 1–10)
const PAYTABLE: Record<number, number[]> = {
  1:  [0, 3.5],
  2:  [0, 1, 12],
  3:  [0, 0, 3, 27],
  4:  [0, 0, 1, 5, 80],
  5:  [0, 0, 0.5, 2, 15, 250],
  6:  [0, 0, 0, 1.5, 4, 60, 1000],
  7:  [0, 0, 0, 1, 3, 25, 150, 2500],
  8:  [0, 0, 0, 0, 2, 10, 60, 600, 7000],
  9:  [0, 0, 0, 0, 1, 5, 30, 120, 1500, 15000],
  10: [0, 0, 0, 0, 0.5, 2, 15, 60, 250, 2500, 25000],
}

// ── State ─────────────────────────────────────────────────────────────────────
const betAmount = ref<number>(1.0)
const activeTab = ref<'bet' | 'paytable' | 'rules'>('bet')
const selectedNumbers = reactive<Set<number>>(new Set())
const drawnSet = reactive<Set<number>>(new Set())
const isDrawing = ref<boolean>(false)
const lastPickCount = ref<number>(0)

interface DrawResult {
  hits: number
  picks: number
  payout: number
  isWin: boolean
}
const lastResult = ref<DrawResult | null>(null)

interface HistoryEntry {
  hits: number
  picks: number
  payout: number
}
const history = ref<HistoryEntry[]>([])

// ── API response type ─────────────────────────────────────────────────────────
interface KenoPlayResponse {
  payout: number
  multiplier: number
  is_win: boolean
  outcome_data: {
    drawn_numbers: number[]
  }
  game_round_id: string
}

// ── Computed ──────────────────────────────────────────────────────────────────
const gridClass = computed<string>(() => {
  return gridSize.value === 40 ? 'grid-40' : 'grid-80'
})

const formattedBalance = computed<string>(() => {
  const b = walletStore.balance?.balance ?? 0
  return '$' + (b / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
})

const canBet = computed<boolean>(() => {
  return !isDrawing.value && selectedNumbers.size > 0 && betAmount.value > 0
})

const jackpotDisplay = computed<string>(() => {
  const picks = selectedNumbers.size
  if (picks === 0) return '—'
  const row = PAYTABLE[picks]
  if (!row) return '—'
  const max = Math.max(...row)
  const bet = betAmount.value || 1
  return '$' + (max * bet).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
})

// Paytable row for current pick count (for Rules tab)
const currentPaytableRow = computed<Record<number, number>>(() => {
  const picks = selectedNumbers.size
  if (!picks || !PAYTABLE[picks]) return {}
  const row: Record<number, number> = {}
  PAYTABLE[picks].forEach((mult, hits) => {
    row[hits] = mult
  })
  return row
})

// ── Ball CSS class helper ─────────────────────────────────────────────────────
function ballClass(n: number): Record<string, boolean> {
  const isSelected = selectedNumbers.has(n)
  const isDrawn = drawnSet.has(n)

  return {
    selected: isSelected && !isDrawing.value && drawnSet.size === 0,
    drawn: isDrawn && !isSelected,
    hit: isDrawn && isSelected,
    missed: !isDrawn && isSelected && drawnSet.size > 0,
  }
}

// ── Selection helpers ─────────────────────────────────────────────────────────
function toggleSelection(n: number): void {
  if (isDrawing.value) return

  // Reset result display when player starts selecting again after a draw
  if (drawnSet.size > 0) {
    drawnSet.clear()
    lastResult.value = null
  }

  if (selectedNumbers.has(n)) {
    selectedNumbers.delete(n)
  } else {
    if (selectedNumbers.size < maxPicks.value) {
      selectedNumbers.add(n)
    } else {
      toastStore.info(`Maximum ${maxPicks.value} picks allowed.`)
    }
  }
}

function clearSelection(): void {
  if (isDrawing.value) return
  selectedNumbers.clear()
  drawnSet.clear()
  lastResult.value = null
}

function quickPick(): void {
  if (isDrawing.value) return

  // Reset previous draw state on new quick pick
  if (drawnSet.size > 0) {
    drawnSet.clear()
    lastResult.value = null
  }

  const needed = maxPicks.value - selectedNumbers.size
  if (needed <= 0) return

  const available: number[] = []
  for (let i = 1; i <= gridSize.value; i++) {
    if (!selectedNumbers.has(i)) available.push(i)
  }

  // Fisher-Yates partial shuffle to pick `needed` random numbers
  for (let i = 0; i < needed && available.length > 0; i++) {
    const idx = Math.floor(Math.random() * (available.length - i)) + i
    ;[available[i], available[idx]] = [available[idx], available[i]]
    selectedNumbers.add(available[i])
  }
}

// ── Bet actions ───────────────────────────────────────────────────────────────
function halveBet(): void {
  betAmount.value = Math.max(0.01, +(betAmount.value / 2).toFixed(2))
}

function doubleBet(): void {
  betAmount.value = +(betAmount.value * 2).toFixed(2)
}

function maxBet(): void {
  const bal = walletStore.balance?.balance ?? 0
  betAmount.value = +(bal / 100).toFixed(2)
}

// ── Place bet + draw animation ────────────────────────────────────────────────
async function placeBet(): Promise<void> {
  if (!canBet.value) return

  const picks = [...selectedNumbers]
  lastPickCount.value = picks.length

  // Validate balance
  const bal = walletStore.balance?.balance ?? 0
  if (betAmount.value * 100 > bal) {
    toastStore.error(t('games.insufficient_balance'))
    return
  }

  isDrawing.value = true
  drawnSet.clear()
  lastResult.value = null

  let drawnNumbers: number[] = []
  let payout = 0
  let isWin = false

  try {
    const res = await apiClient.post<KenoPlayResponse>('/api/games/keno/play', {
      amount: betAmount.value.toFixed(2),
      options: { picks },
    })

    drawnNumbers = res.data.outcome_data.drawn_numbers
    payout = res.data.payout
    isWin = res.data.is_win
  } catch (err: unknown) {
    const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
    toastStore.error(msg ?? 'Bet failed. Please try again.')
    isDrawing.value = false
    return
  }

  // ── Sequential draw animation ─────────────────────────────────────────────
  if (speedMode.value === 'instant') {
    // Reveal all numbers at once, then yield one animation frame
    for (const num of drawnNumbers) {
      drawnSet.add(num)
    }
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))
  } else {
    for (const num of drawnNumbers) {
      drawnSet.add(num)
      const isHit = picks.includes(num)
      playBeep(isHit ? 'hit' : 'draw')
      await sleep(speedDelayMs.value)
    }
  }

  // ── Calculate hits and show result ───────────────────────────────────────
  const hits = picks.filter((p) => drawnSet.has(p)).length

  lastResult.value = { hits, picks: picks.length, payout, isWin }

  // Push to history (max 20 entries)
  history.value.unshift({ hits, picks: picks.length, payout })
  if (history.value.length > 20) history.value.pop()

  // Refresh wallet balance
  walletStore.applyGameResult(betAmount.value, payout)

  if (isWin) {
    toastStore.success(`🎉 ${hits}/${picks.length} matches — won $${payout.toFixed(2)}!`)
  } else if (hits > 0) {
    toastStore.info(`${hits}/${picks.length} matches — no payout this time.`)
  } else {
    toastStore.error(`No matches — better luck next time!`)
  }

  isDrawing.value = false
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

// ── Preference persistence ────────────────────────────────────────────────────
onMounted(() => {
  try {
    const saved = sessionStorage.getItem('keno_prefs')
    if (saved) {
      const prefs = JSON.parse(saved) as { speedMode?: SpeedMode; soundEnabled?: boolean }
      if (prefs.speedMode) speedMode.value = prefs.speedMode
      if (typeof prefs.soundEnabled === 'boolean') soundEnabled.value = prefs.soundEnabled
    }
  } catch {
    // ignore
  }
})

watch([speedMode, soundEnabled], () => {
  try {
    sessionStorage.setItem('keno_prefs', JSON.stringify({ speedMode: speedMode.value, soundEnabled: soundEnabled.value }))
  } catch {
    // ignore
  }
})
</script>

<style scoped>
/* ── Layout ──────────────────────────────────────────────────────────────────── */
.keno-game {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.game-topbar {
  height: 48px;
  background: var(--bg-secondary, #1a2c38);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  padding: 0 16px;
  gap: 12px;
  flex-shrink: 0;
}

.btn-back {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 8px;
  background: var(--bg-tertiary, #213743);
  border: none;
  cursor: pointer;
  transition: all 150ms ease;
  font-family: inherit;
}
.btn-back:hover { background: var(--bg-hover, #2a4455); color: var(--color-on-dark, #fff); }

.game-title-bar {
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
}

.game-tag {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  padding: 2px 8px;
  border-radius: 9999px;
  background: rgba(0, 231, 1, 0.1);
  color: var(--accent-primary, #00e701);
  border: 1px solid rgba(0, 231, 1, 0.2);
}

.game-topbar-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 6px;
}

.icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--bg-tertiary, #213743);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  cursor: pointer;
  color: var(--text-secondary, #b1bad3);
  transition: all 150ms ease;
  border: none;
}
.icon-btn:hover { background: var(--bg-hover, #2a4455); color: var(--color-on-dark, #fff); }

/* ── Game body split ─────────────────────────────────────────────────────────── */
.game-body {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.game-canvas-area {
  flex: 1;
  background: var(--bg-game, #0d1b2a);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  flex-direction: column;
}

.canvas-bg-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(0, 231, 1, 0.025) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 231, 1, 0.025) 1px, transparent 1px);
  background-size: 40px 40px;
  pointer-events: none;
}

.canvas-bg-glow {
  position: absolute;
  pointer-events: none;
  right: -100px;
  top: -100px;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(0, 231, 1, 0.05) 0%, transparent 65%);
}

/* ── Keno Board ──────────────────────────────────────────────────────────────── */
.keno-board {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 20px;
  z-index: 1;
  width: 100%;
  max-width: 620px;
}

/* Stats row above grid */
.keno-stats-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0 4px;
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
  min-height: 22px;
}

.pick-num {
  color: var(--accent-primary, #00e701);
  font-weight: 700;
}

.keno-match-count {
  font-family: 'JetBrains Mono', monospace;
  font-size: 13px;
  font-weight: 600;
}
.keno-match-count.match-win { color: var(--color-success, #00e701); }
.keno-match-count.match-loss { color: var(--color-danger, #ed4245); }
.match-num { font-weight: 800; }
.match-payout { color: var(--color-success, #00e701); }

/* ── Keno Grid ───────────────────────────────────────────────────────────────── */
.keno-grid {
  display: grid;
  gap: 5px;
  width: 100%;
}
.grid-80 { grid-template-columns: repeat(10, 1fr); }
.grid-40 { grid-template-columns: repeat(8, 1fr); }

.keno-ball {
  aspect-ratio: 1;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  border: 2px solid rgba(255, 255, 255, 0.1);
  background: var(--bg-tertiary, #213743);
  transition: all 150ms ease;
  font-family: 'JetBrains Mono', monospace;
  color: var(--text-secondary, #b1bad3);
  padding: 0;
}

.keno-ball:hover:not(:disabled):not(.drawn):not(.selected):not(.hit) {
  background: var(--bg-hover, #2a4455);
  color: var(--color-on-dark, #fff);
}

.keno-ball:disabled {
  cursor: default;
}

/* Selected by player (before draw) */
.keno-ball.selected {
  background: rgba(0, 231, 1, 0.2);
  border-color: var(--accent-primary, #00e701);
  color: var(--accent-primary, #00e701);
  box-shadow: 0 0 10px rgba(0, 231, 1, 0.3);
}

/* Drawn but not selected */
.keno-ball.drawn {
  background: rgba(255, 215, 0, 0.2);
  border-color: var(--accent-gold, #ffd700);
  color: var(--accent-gold, #ffd700);
  box-shadow: 0 0 10px rgba(255, 215, 0, 0.3);
  animation: winPop 0.3s ease;
}

/* Drawn + selected (a match / hit) */
.keno-ball.hit {
  background: rgba(0, 231, 1, 0.35);
  border-color: var(--accent-primary, #00e701);
  color: var(--color-on-dark, #fff);
  box-shadow: 0 0 16px rgba(0, 231, 1, 0.5);
  animation: winPop 0.3s ease;
}

/* Selected but not drawn (missed) */
.keno-ball.missed {
  background: rgba(237, 66, 69, 0.12);
  border-color: rgba(237, 66, 69, 0.4);
  color: rgba(255, 255, 255, 0.35);
}

@keyframes winPop {
  0% { transform: scale(0.8); opacity: 0; }
  60% { transform: scale(1.15); }
  100% { transform: scale(1); opacity: 1; }
}

/* ── History strip ───────────────────────────────────────────────────────────── */
.keno-history {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  overflow: hidden;
}

.history-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-tertiary, #7f8fa4);
  flex-shrink: 0;
}

.history-strip {
  display: flex;
  gap: 4px;
  overflow-x: auto;
  flex: 1;
  padding-bottom: 2px;
  scrollbar-width: none;
}
.history-strip::-webkit-scrollbar { display: none; }

.history-pill {
  flex-shrink: 0;
  padding: 3px 8px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 700;
  font-family: 'JetBrains Mono', monospace;
  border: 1px solid transparent;
}
.history-pill.pill-win {
  background: rgba(0, 231, 1, 0.1);
  border-color: rgba(0, 231, 1, 0.25);
  color: var(--accent-primary, #00e701);
}
.history-pill.pill-loss {
  background: rgba(237, 66, 69, 0.08);
  border-color: rgba(237, 66, 69, 0.2);
  color: var(--text-tertiary, #7f8fa4);
}

/* ── Sidebar ─────────────────────────────────────────────────────────────────── */
.game-sidebar {
  width: 280px;
  background: var(--bg-secondary, #1a2c38);
  border-left: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
}

.game-tabs {
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}

.game-tab {
  flex: 1;
  padding: 13px 4px;
  font-size: 11px;
  font-weight: 700;
  color: var(--text-tertiary, #7f8fa4);
  text-align: center;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 150ms ease;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: none;
  border-top: none;
  border-left: none;
  border-right: none;
  font-family: inherit;
}
.game-tab.active {
  color: var(--accent-primary, #00e701);
  border-bottom-color: var(--accent-primary, #00e701);
}
.game-tab:hover:not(.active) { color: var(--text-secondary, #b1bad3); }

.game-tab-content {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 0;
}
.game-tab-content::-webkit-scrollbar { width: 3px; }
.game-tab-content::-webkit-scrollbar-thumb { background: var(--bg-tertiary, #213743); border-radius: 4px; }

/* ── Bet controls ────────────────────────────────────────────────────────────── */
.ctrl-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-tertiary, #7f8fa4);
  margin-bottom: 6px;
  display: block;
}

.bet-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 6px;
}

.bet-currency {
  position: absolute;
  left: 10px;
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  pointer-events: none;
}

.bet-input {
  background: var(--bg-primary, #0f1923);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 10px 10px 10px 24px;
  color: var(--color-on-dark, #fff);
  font-family: 'JetBrains Mono', monospace;
  font-size: 14px;
  font-weight: 600;
  width: 100%;
  transition: border-color 150ms ease;
}
.bet-input:focus { outline: none; border-color: rgba(20, 117, 225, 0.5); }
.bet-input:disabled { opacity: 0.5; }

.bet-shortcuts {
  display: flex;
  gap: 4px;
  margin-bottom: 14px;
}

.bet-short {
  flex: 1;
  padding: 6px 0;
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  font-size: 11px;
  font-weight: 700;
  border-radius: 4px;
  cursor: pointer;
  border: none;
  transition: all 150ms ease;
  text-align: center;
  font-family: inherit;
}
.bet-short:hover:not(:disabled) { background: var(--bg-hover, #2a4455); color: var(--color-on-dark, #fff); }
.bet-short:disabled { opacity: 0.4; cursor: not-allowed; }

/* Quick pick + clear */
.keno-quick-row {
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
}

.btn-quick,
.btn-clear {
  flex: 1;
  padding: 9px 4px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  border: none;
  transition: all 150ms ease;
  font-family: inherit;
}

.btn-quick {
  background: rgba(20, 117, 225, 0.15);
  color: var(--accent-secondary, #1475e1);
  border: 1px solid rgba(20, 117, 225, 0.25);
}
.btn-quick:hover:not(:disabled) {
  background: rgba(20, 117, 225, 0.25);
}

.btn-clear {
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.btn-clear:hover:not(:disabled) { background: var(--bg-hover, #2a4455); color: var(--color-on-dark, #fff); }

.btn-quick:disabled,
.btn-clear:disabled { opacity: 0.4; cursor: not-allowed; }

/* Stat pills */
.stat-pills {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  margin-bottom: 14px;
}

.stat-pill {
  background: var(--bg-tertiary, #213743);
  border-radius: 8px;
  padding: 8px 10px;
  border: 1px solid rgba(255, 255, 255, 0.04);
}

.sp-label {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-tertiary, #7f8fa4);
  margin-bottom: 3px;
}

.sp-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 13px;
  font-weight: 700;
  color: var(--accent-primary, #00e701);
}
.sp-value.gold { color: var(--accent-gold, #ffd700); }

/* Action button */
.btn-action {
  width: 100%;
  padding: 14px;
  background: var(--accent-primary, #00e701);
  color: var(--color-on-accent, #000);
  font-size: 15px;
  font-weight: 800;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 150ms ease;
  letter-spacing: 0.03em;
  margin-bottom: 8px;
  font-family: inherit;
}
.btn-action:hover:not(:disabled) {
  background: var(--accent-primary-hover, #00cc01);
  box-shadow: 0 0 24px rgba(0, 231, 1, 0.35);
  transform: translateY(-1px);
}
.btn-action:disabled { opacity: 0.4; cursor: not-allowed; box-shadow: none; transform: none; }
.btn-action:active:not(:disabled) { transform: translateY(0); }

/* Balance row */
.balance-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  margin-top: 4px;
}
.bal-label { font-size: 11px; color: var(--text-tertiary, #7f8fa4); }
.bal-value { font-family: 'JetBrains Mono', monospace; font-size: 13px; font-weight: 700; color: var(--text-secondary, #b1bad3); }
.bal-value.green { color: var(--color-success, #00e701); }

/* ── Rules tab ───────────────────────────────────────────────────────────────── */
.rules-section { margin-bottom: 16px; }
.rules-title {
  font-size: 12px;
  font-weight: 700;
  color: var(--color-on-dark, #fff);
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding-bottom: 6px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}
.rules-title-icon { font-size: 14px; }
.rules-list { list-style: none; display: flex; flex-direction: column; gap: 5px; }
.rules-item {
  font-size: 12px;
  color: var(--text-secondary, #b1bad3);
  padding-left: 14px;
  position: relative;
  line-height: 1.55;
}
.rules-item::before { content: '▸'; position: absolute; left: 0; color: var(--accent-primary, #00e701); font-size: 10px; top: 1px; }
.rules-note {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
  background: var(--bg-tertiary, #213743);
  border-radius: 8px;
  padding: 10px;
  margin-top: 10px;
  line-height: 1.6;
  border-left: 2px solid var(--accent-primary, #00e701);
}

/* Paytable reference */
.paytable-ref {
  margin-bottom: 16px;
}
.paytable-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--text-tertiary, #7f8fa4);
  margin-bottom: 8px;
}
.paytable-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 8px;
  border-radius: 6px;
  font-size: 12px;
  transition: background 150ms ease;
}
.paytable-row.pt-highlighted {
  background: rgba(0, 231, 1, 0.1);
  border: 1px solid rgba(0, 231, 1, 0.2);
}
.pt-hits { color: var(--text-secondary, #b1bad3); }
.pt-mult { font-family: 'JetBrains Mono', monospace; font-weight: 700; }
.pt-win { color: var(--accent-gold, #ffd700); }
.pt-zero { color: var(--text-disabled, #4a5568); }

.fair-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: rgba(20, 117, 225, 0.1);
  border: 1px solid rgba(20, 117, 225, 0.2);
  border-radius: 9999px;
  padding: 4px 10px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: var(--accent-secondary, #1475e1);
  margin-top: 12px;
  cursor: pointer;
  align-self: flex-start;
}
.fair-tag:hover { background: rgba(20, 117, 225, 0.18); }

/* ── Sound toggle active state ───────────────────────────────────────────────── */
.icon-btn-active {
  background: rgba(0, 231, 1, 0.12) !important;
  color: var(--accent-primary, #00e701) !important;
  border: 1px solid rgba(0, 231, 1, 0.25) !important;
}

/* ── Speed modes ─────────────────────────────────────────────────────────────── */
.speed-modes {
  display: flex;
  gap: 4px;
  margin-bottom: 14px;
}

.speed-btn {
  flex: 1;
  padding: 7px 4px;
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  font-size: 11px;
  font-weight: 700;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.06);
  transition: all 150ms ease;
  font-family: inherit;
  text-align: center;
}
.speed-btn:hover:not(:disabled):not(.active) {
  background: var(--bg-hover, #2a4455);
  color: var(--color-on-dark, #fff);
}
.speed-btn.active {
  background: rgba(0, 231, 1, 0.15);
  color: var(--accent-primary, #00e701);
  border-color: rgba(0, 231, 1, 0.3);
}
.speed-btn:disabled { opacity: 0.4; cursor: not-allowed; }

/* ── Paytable toggle button (canvas area) ───────────────────────────────────── */
.keno-paytable-toggle {
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 0 4px;
}

.btn-paytable-toggle {
  padding: 5px 14px;
  background: rgba(20, 117, 225, 0.08);
  border: 1px solid rgba(20, 117, 225, 0.2);
  border-radius: 9999px;
  color: var(--accent-secondary, #1475e1);
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: all 150ms ease;
  font-family: inherit;
}
.btn-paytable-toggle:hover {
  background: rgba(20, 117, 225, 0.15);
}

/* ── Sidebar paytable tab ────────────────────────────────────────────────────── */
.sidebar-paytable {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sp-header {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 8px;
}
.sp-header-title {
  font-size: 12px;
  font-weight: 700;
  color: var(--color-on-dark, #fff);
}
.sp-header-sub {
  font-size: 10px;
  color: var(--text-tertiary, #7f8fa4);
}

.sp-picks-row {
  border-radius: 6px;
  padding: 4px 6px;
  border: 1px solid transparent;
  transition: all 150ms ease;
}
.sp-picks-row.sp-picks-active {
  background: rgba(0, 231, 1, 0.06);
  border-color: rgba(0, 231, 1, 0.15);
}

.sp-picks-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-tertiary, #7f8fa4);
  margin-bottom: 4px;
}
.sp-picks-active .sp-picks-label {
  color: var(--accent-primary, #00e701);
}

.sp-hits-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
}

.sp-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 36px;
  padding: 3px 2px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  background: var(--bg-primary, #0f1923);
  cursor: default;
}
.sp-cell-win {
  border-color: rgba(255, 215, 0, 0.2);
  background: rgba(255, 215, 0, 0.05);
}
.sp-cell-zero {
  opacity: 0.4;
}
.sp-cell-highlight {
  background: rgba(0, 231, 1, 0.2);
  border-color: rgba(0, 231, 1, 0.5);
}
.sp-cell-hits {
  font-size: 8px;
  color: var(--text-tertiary, #7f8fa4);
  line-height: 1;
}
.sp-cell-mult {
  font-size: 9px;
  font-weight: 700;
  font-family: 'JetBrains Mono', monospace;
  color: var(--accent-gold, #ffd700);
  line-height: 1.4;
}
.sp-cell-zero .sp-cell-mult { color: var(--text-disabled, #4a5568); }

/* ── Paytable overlay modal ──────────────────────────────────────────────────── */
.paytable-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.paytable-modal {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  width: 100%;
  max-width: 720px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.paytable-modal-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}
.paytable-modal-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--color-on-dark, #fff);
}
.paytable-modal-sub {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
}
.paytable-close {
  margin-left: auto;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: var(--bg-tertiary, #213743);
  border: none;
  color: var(--text-secondary, #b1bad3);
  cursor: pointer;
  font-size: 13px;
  transition: all 150ms ease;
  font-family: inherit;
}
.paytable-close:hover { background: var(--bg-hover, #2a4455); color: var(--color-on-dark, #fff); }

.paytable-modal-body {
  overflow-y: auto;
  padding: 16px 20px;
  flex: 1;
}
.paytable-modal-body::-webkit-scrollbar { width: 4px; }
.paytable-modal-body::-webkit-scrollbar-thumb { background: var(--bg-tertiary, #213743); border-radius: 4px; }

.paytable-legend {
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
  font-size: 11px;
}
.pl-current { color: var(--accent-primary, #00e701); }
.pl-result { color: var(--accent-gold, #ffd700); }
.pl-current::before { content: '■ '; }
.pl-result::before { content: '■ '; }

/* Paytable matrix */
.paytable-matrix {
  overflow-x: auto;
  font-family: 'JetBrains Mono', monospace;
}

.pm-row {
  display: flex;
  align-items: center;
  border-radius: 4px;
  margin-bottom: 2px;
  min-width: max-content;
}
.pm-row:hover { background: rgba(255, 255, 255, 0.02); }
.pm-row-active { background: rgba(0, 231, 1, 0.05) !important; }

.pm-header-row {
  margin-bottom: 4px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  padding-bottom: 4px;
}

.pm-picks-col {
  width: 48px;
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  color: var(--text-tertiary, #7f8fa4);
  padding: 4px 6px;
}
.pm-picks-active {
  color: var(--accent-primary, #00e701) !important;
}
.pm-header-cell {
  color: var(--text-tertiary, #7f8fa4) !important;
  font-size: 10px;
  font-weight: 700;
}

.pm-cell {
  width: 52px;
  flex-shrink: 0;
  font-size: 11px;
  text-align: center;
  padding: 4px 2px;
  border-radius: 4px;
  color: var(--text-disabled, #4a5568);
}
.pm-cell-win {
  color: var(--accent-gold, #ffd700);
}
.pm-cell-zero {
  color: var(--text-disabled, #4a5568);
}
.pm-cell-na {
  color: rgba(255, 255, 255, 0.06);
}
.pm-cell-result {
  background: rgba(255, 215, 0, 0.18);
  border: 1px solid rgba(255, 215, 0, 0.4);
  color: var(--color-on-dark, #fff);
  font-weight: 700;
}

/* Modal fade transition */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 200ms ease;
}
.modal-fade-enter-active .paytable-modal,
.modal-fade-leave-active .paytable-modal {
  transition: transform 200ms ease, opacity 200ms ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
.modal-fade-enter-from .paytable-modal,
.modal-fade-leave-to .paytable-modal {
  transform: scale(0.95);
  opacity: 0;
}

/* ── Responsive ──────────────────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .game-body {
    flex-direction: column;
  }
  .game-sidebar {
    width: 100%;
    border-left: none;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    max-height: 320px;
  }
  .keno-grid { gap: 4px; }
  .keno-ball { font-size: 10px; }
  .paytable-modal { max-height: 90vh; }
}
</style>
